import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SettingsInput } from '@mlopsmena/content'
import { api, ApiError, type Item } from '../lib/api'
import { SETTINGS_FIELDS } from '../lib/types'
import { toFormValues } from '../lib/formValues'
import { Fields, FormLangContext } from '../fields/Fields'
import { Notice, PageHeader, Spinner } from '../components/ui'

/** Copy that belongs to no single item: the team summary and the community numbers. */
export default function SettingsPage() {
  const query = useQuery({ queryKey: ['settings'], queryFn: () => api.get<{ item: Item }>('/content/settings').then((r) => r.item) })
  if (query.isPending) return <Spinner />
  if (query.isError) return <Notice>{(query.error as Error).message}</Notice>
  return <SettingsForm key={query.data.updatedAt} item={query.data} />
}

function SettingsForm({ item }: { item: Item }) {
  const qc = useQueryClient()
  const [lang, setLang] = useState<'en' | 'ar'>('en')
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const form = useForm<Record<string, unknown>>({ resolver: zodResolver(SettingsInput as never), defaultValues: toFormValues(SETTINGS_FIELDS, item) })

  async function save(values: Record<string, unknown>) {
    try {
      const res = await api.put<{ item: Item }>('/content/settings', { ...values, updatedAt: item.updatedAt })
      qc.setQueryData(['settings'], res.item)
      await qc.invalidateQueries({ queryKey: ['publish', 'status'] })
      setNotice({ kind: 'ok', text: 'Saved. It goes live on the next publish.' })
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof ApiError ? err.message : String(err) })
    }
  }

  return (
    <FormProvider {...form}>
      <FormLangContext.Provider value={lang}>
        <form onSubmit={form.handleSubmit(save)} className="space-y-4">
          <PageHeader title="Site copy">
            <div className="flex rounded-lg border border-line p-0.5">
              {(['en', 'ar'] as const).map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                        className={`rounded-md px-3 py-1 text-xs font-semibold ${lang === l ? 'bg-accent text-accent-fg' : 'text-muted'}`}>
                  {l === 'en' ? 'English' : 'العربية'}
                </button>
              ))}
            </div>
            <button className="btn-primary" disabled={form.formState.isSubmitting}>Save</button>
          </PageHeader>
          {notice && <Notice kind={notice.kind}>{notice.text}</Notice>}
          <div className="card p-5"><Fields fields={SETTINGS_FIELDS} /></div>
        </form>
      </FormLangContext.Provider>
    </FormProvider>
  )
}
